/**
 * SRES · CPR Report-Run Panel  (Sprint P24 · Feb 2026)
 *
 * The user-facing UI for the new zero-tolerance CPR pipeline.
 *
 *   · Opens a report run (freezes inputs & runs validation gates).
 *   · Shows every blocking issue with error_code + module + remediation.
 *   · Blocks the "Publish CPR PDF" button until every blocker is cleared.
 *   · Displays the frozen snapshot (block model, drillhole count, density,
 *     CP declaration) exactly as it will be printed in the PDF.
 *   · Lets the user download the immutable PDF once published.
 *
 * All UI text and structure is fully wired to `data-testid`s for testing.
 */
import { useState } from "react";
import CPRPreviewModal from "./CPRPreviewModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Warning, CheckCircle, ShieldWarning, Lock, ArrowClockwise,
  FilePdf, FloppyDisk, ClockCounterClockwise, Circle, Signature,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";


// ───────────────────────────────────────────────────────────────
// Iter98g · Live-Compile Log
// ───────────────────────────────────────────────────────────────
const PHASE_META = {
  started:          { label: "Initialising",        tone: "info" },
  loading_snapshot: { label: "Loading snapshot",    tone: "info" },
  validating:       { label: "Validating",          tone: "info" },
  loading_project:  { label: "Loading project",     tone: "info" },
  rendering_pdf:    { label: "Rendering PDF",       tone: "info" },
  pdf_ready:        { label: "PDF ready",           tone: "info" },
  persisting:       { label: "Persisting artifact", tone: "info" },
  done:             { label: "Done",                tone: "ok" },
  blocked:          { label: "Blocked",             tone: "warn" },
  superseded:       { label: "Superseded",          tone: "warn" },
  error:            { label: "Failed",              tone: "bad" },
  idle:             { label: "Idle",                tone: "info" },
};

function LiveCompileLog({ pid, runId, active, autoHide, onRetry, retryDisabled }) {
  const q = useQuery({
    queryKey: ["cpr-status", pid, runId],
    queryFn:  () => api.get(`/projects/${pid}/wizard/cpr-pdf/status/${runId}`).then((r) => r.data),
    enabled:  !!pid && !!runId && (active || !autoHide),
    refetchInterval: (result) => {
      const s = result?.state?.data;
      if (!active && s?.finished) return false;   // stop polling once done and idle
      return 700;
    },
    retry: 0,
  });
  const s = q.data;
  if (!s || (autoHide && !active && s.phase === "idle")) return null;
  const meta = PHASE_META[s.phase] || PHASE_META.idle;
  const isError   = meta.tone === "bad";
  const isWarn    = meta.tone === "warn";
  const isDone    = meta.tone === "ok";
  const barColour = isError ? "bg-rose-500"
                   : isWarn ? "bg-amber-500"
                   : isDone ? "bg-emerald-500"
                   : "bg-brand-blue";

  const recent = (s.log || []).slice(-6).reverse();
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2"
           data-testid="cpr-live-compile-log">
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-2 h-2 rounded-full ${
          active && !s.finished
            ? "bg-brand-blue animate-pulse"
            : isDone ? "bg-emerald-500"
              : isError ? "bg-rose-500"
                : "bg-slate-400"
        }`} />
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-700">
          Compile status
        </div>
        <div className={`text-[11.5px] font-semibold ${
          isError ? "text-rose-700"
            : isWarn ? "text-amber-700"
              : isDone ? "text-emerald-700"
                : "text-slate-800"
        }`} data-testid="cpr-live-compile-phase">
          · {meta.label}
        </div>
        <div className="ml-auto text-[10.5px] font-mono text-slate-500"
                data-testid="cpr-live-compile-progress">
          {Number(s.progress || 0)}%
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden ring-1 ring-slate-200">
        <div className={`h-full ${barColour} transition-all duration-500`}
                style={{ width: `${Math.min(100, Number(s.progress || 0))}%` }}
                data-testid="cpr-live-compile-progress-bar" />
      </div>
      {s.note && (
        <div className={`mt-1 text-[11px] ${isError ? "text-rose-700" : "text-slate-600"}`}>
          {s.note}
        </div>
      )}
      {s.error && (
        <div className="mt-1 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-800 font-mono flex items-start gap-2"
              data-testid="cpr-live-compile-error">
          <span className="flex-1 whitespace-pre-wrap break-words">{s.error}</span>
          {onRetry && (
            <div className="flex flex-col gap-1 shrink-0">
              <button
                type="button"
                onClick={() => onRetry(false)}
                disabled={retryDisabled}
                className="rounded border border-rose-300 bg-white px-2 py-0.5 text-[10.5px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                data-testid="cpr-live-compile-retry">
                {retryDisabled ? "Retrying…" : "Retry"}
              </button>
              {/* Iter98i · Retry with Fresh Snapshot — opens a
                  successor run (re-freezing source data) so drift
                  errors auto-resolve. */}
              <button
                type="button"
                onClick={() => onRetry(true)}
                disabled={retryDisabled}
                title="Open a new run with a fresh snapshot from the current project state"
                className="rounded border border-brand-blue bg-white px-2 py-0.5 text-[10.5px] font-semibold text-brand-blue hover:bg-blue-50 disabled:opacity-50"
                data-testid="cpr-live-compile-retry-fresh">
                Fresh snapshot
              </button>
            </div>
          )}
        </div>
      )}
      {recent.length > 0 && (
        <details className="mt-1.5">
          <summary className="text-[10.5px] text-slate-500 cursor-pointer hover:text-slate-800">
            Show phase log ({recent.length})
          </summary>
          <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto text-[10.5px] font-mono text-slate-600">
            {recent.map((e, i) => (
              <li key={i} className="flex items-center gap-1.5"
                    data-testid="cpr-live-compile-log-entry">
                <span className="text-slate-400">{String(e.ts || "").slice(11, 19)}</span>
                <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                  e.error ? "bg-rose-500" : "bg-brand-blue"
                }`} />
                <span className={e.error ? "text-rose-700" : "text-slate-700"}>
                  {(PHASE_META[e.phase] || {}).label || e.phase}
                  {e.progress != null && ` · ${e.progress}%`}
                </span>
                {e.note && <span className="text-slate-500 truncate">— {e.note}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


/**
 * REST helpers — thin wrappers over the /api/projects/:pid/report-runs suite.
 */
export const reportRunApi = {
  active: (pid) => api.get(`/projects/${pid}/report-runs/active`).then((r) => r.data),
  list:   (pid) => api.get(`/projects/${pid}/report-runs`).then((r) => r.data),
  open:   (pid) => api.post(`/projects/${pid}/report-runs`).then((r) => r.data),
  successor: (pid, predecessorId, reason) =>
    api.post(`/projects/${pid}/report-runs/${predecessorId}/successor`,
             { reason: (reason || "").trim() || null }).then((r) => r.data),
  refreshSupersession: (pid) =>
    api.post(`/projects/${pid}/report-runs/refresh-supersession`).then((r) => r.data),
  validate: (pid, runId) => api.post(`/projects/${pid}/report-runs/${runId}/validate`).then((r) => r.data),
  publish:  (pid, runId, meta = {}) =>
    api.post(`/projects/${pid}/report-runs/${runId}/publish`, meta).then((r) => r.data),
  downloadPdf: async (pid, runId) => {
    const res = await api.get(`/projects/${pid}/report-runs/${runId}/pdf`,
      { responseType: "blob", timeout: 180_000 });
    const filename = res.headers["content-disposition"]
      ?.split("filename=")[1]?.replace(/"/g, "") || `CPR_${runId}.pdf`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return filename;
  },
};


const SEVERITY_STYLES = {
  critical: {
    row: "bg-rose-50 border-rose-200 text-rose-800",
    icon: <Warning size={14} weight="fill" className="text-rose-600" />,
    tag: "bg-rose-600",
  },
  error: {
    row: "bg-orange-50 border-orange-200 text-orange-800",
    icon: <Warning size={14} weight="fill" className="text-orange-600" />,
    tag: "bg-orange-500",
  },
  warning: {
    row: "bg-amber-50 border-amber-200 text-amber-800",
    icon: <ShieldWarning size={14} weight="fill" className="text-amber-600" />,
    tag: "bg-amber-500",
  },
  info: {
    row: "bg-sky-50 border-sky-200 text-sky-800",
    icon: <Circle size={12} weight="fill" className="text-sky-500" />,
    tag: "bg-sky-500",
  },
};


function ValidationRow({ v }) {
  const s = SEVERITY_STYLES[v.severity] || SEVERITY_STYLES.warning;
  return (
    <div className={`border rounded p-2.5 flex items-start gap-2 ${s.row}`}
         data-testid={`report-run-validation-${v.error_code}`}>
      <div className="mt-0.5">{s.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-[11px] font-mono font-bold">{v.error_code}</code>
          <Badge className={`text-white text-[9px] ${s.tag}`}>{v.severity}</Badge>
          {v.blocking && (
            <Badge className="bg-rose-800 text-white text-[9px]"
                    data-testid={`report-run-blocker-${v.error_code}`}>BLOCKING</Badge>
          )}
          <span className="text-[9px] font-mono opacity-70">{v.module}</span>
        </div>
        <div className="text-[12px] mt-1">{v.message}</div>
        {v.remediation && (
          <div className="text-[11px] opacity-80 mt-1">
            <b>Remediation:</b> {v.remediation}
          </div>
        )}
        {v.actual_value !== null && v.actual_value !== undefined && (
          <div className="text-[10px] font-mono opacity-70 mt-1">
            actual: <code>{typeof v.actual_value === "object"
              ? JSON.stringify(v.actual_value) : String(v.actual_value)}</code>
            {" · "}expected: <code>{v.expected_requirement}</code>
          </div>
        )}
      </div>
    </div>
  );
}


function SnapshotSummary({ snap }) {
  if (!snap) return null;
  const bm = snap.block_model || {};
  const dm = snap.density_model || {};
  const dh = snap.drillholes || {};
  const decls = snap.cp_declarations || [];
  const proj = snap.project || {};
  const rows = [
    ["Project",         proj.name],
    ["Commodity",       proj.commodity],
    ["Block model",     bm.total_blocks
                          ? `${bm.total_blocks} blocks (${bm.ore_blocks} ore / ${bm.waste_blocks} waste)`
                          : "—"],
    ["Estimation method", bm.method || "—"],
    ["Block size (E×N×Z)", Array.isArray(bm.block_size)
                              ? bm.block_size.map((x) => `${x} m`).join(" × ")
                              : "—"],
    ["Reporting cut-off", bm.cutoff_grade != null
                              ? `${Number(bm.cutoff_grade).toFixed(3)} ${proj.commodity === "Au" || proj.commodity === "Ag" ? "g/t" : "%"}`
                              : "—"],
    ["Drillholes",      `${dh.count || 0} unique holes`],
    ["Density model",   dm.default_density
                            ? `${dm.default_density} t/m³ (${dm.approved ? "approved" : "unapproved"})`
                            : "— missing —"],
    ["Competent Person", decls.length
                            ? `${decls[0].person_name || decls[0].firm || decls[0].cp_profile_id?.slice(0,8) || "unnamed"} · ${decls[0].qualification || ""} · ${decls[0].firm || ""}`
                            : "— no signed declaration —"],
    ["Reporting standard", decls[0]?.reporting_standard || "—"],
    ["Sections covered",   (decls[0]?.sections_covered || []).join(", ") || "—"],
    ["Snapshot captured",  snap.captured_at?.slice(0, 19).replace("T", " ") || "—"],
  ];
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12px]"
         data-testid="report-run-snapshot">
      {rows.map(([k, val]) => (
        <div key={k} className="contents">
          <span className="text-slate-500">{k}</span>
          <span className="text-slate-800 font-mono truncate"
                title={String(val)}>{val || "—"}</span>
        </div>
      ))}
    </div>
  );
}


function GoldUnitBox({ gold }) {
  if (!gold) return null;
  if (gold.commodity !== "Au") return null;
  return (
    <div className="mt-3 border border-yellow-500 bg-yellow-50 rounded p-3"
         data-testid="report-run-gold-summary">
      <div className="text-[10px] font-mono uppercase tracking-widest text-yellow-800 mb-1">
        Gold contained-metal summary
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] font-mono">
        <div><span className="text-slate-500">Cut-off:</span> <b>{gold.grade_gpt} g/t</b></div>
        <div><span className="text-slate-500">Tonnes:</span> <b>{Number(gold.tonnes || 0).toLocaleString()} t</b></div>
        <div><span className="text-slate-500">Contained metal:</span> <b>{Number(gold.contained_grams || 0).toLocaleString()} g</b></div>
        <div><span className="text-slate-500">Ounces (troy):</span> <b>{Number(gold.contained_oz || 0).toLocaleString()} Oz</b></div>
        <div><span className="text-slate-500">Kilo-ounces:</span> <b>{gold.contained_koz} kOz</b></div>
      </div>
    </div>
  );
}


export default function CPRReportRunPanel({ pid }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [previewRunId, setPreviewRunId] = useState(null);

  const activeQ = useQuery({
    queryKey: ["report-run-active", pid],
    queryFn: () => reportRunApi.active(pid),
    enabled: !!pid,
    staleTime: 15_000,
  });

  const openRunMut = useMutation({
    mutationFn: () => reportRunApi.open(pid),
    onSuccess: () => {
      toast.success("New report run opened — inputs frozen");
      qc.invalidateQueries({ queryKey: ["report-run-active", pid] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const revalidateMut = useMutation({
    mutationFn: (runId) => reportRunApi.validate(pid, runId),
    onSuccess: (data) => {
      const blocking = (data?.blocking || []).length;
      if (blocking) toast.warning(`Still ${blocking} blocking issue(s)`);
      else toast.success("All validations passed — ready to publish");
      qc.invalidateQueries({ queryKey: ["report-run-active", pid] });
    },
  });

  const publishMut = useMutation({
    mutationFn: (runId) => reportRunApi.publish(pid, runId, {}),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["report-run-active", pid] });
      if (data?.status === "published") {
        toast.success(`CPR published (${data.run_id} · rev ${data.revision})`);
      } else if (data?.status === "blocked") {
        toast.error(`Publish blocked — ${(data.blocking || []).length} unresolved issue(s)`);
      } else if (data?.status === "superseded") {
        toast.error("Inputs have drifted — this run has been superseded. Open a new run.");
      } else if (data?.status === "error") {
        toast.error(`Publish error: ${data.error}`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  const successorMut = useMutation({
    mutationFn: ({ predecessorId, reason }) =>
      reportRunApi.successor(pid, predecessorId, reason),
    onSuccess: () => {
      toast.success("Successor run opened — new frozen snapshot");
      qc.invalidateQueries({ queryKey: ["report-run-active", pid] });
      setSuccessorPrompt(null);
      setSuccessorReason("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed"),
  });

  // Iter98j · Fresh-snapshot successor prompt — captures a one-line
  // reason and shows which fields will re-freeze from current project
  // state before spawning the successor.
  const [successorPrompt, setSuccessorPrompt] = useState(null);
  const [successorReason, setSuccessorReason] = useState("");

  const downloadPdf = async (runId) => {
    setBusy("pdf");
    try {
      await reportRunApi.downloadPdf(pid, runId);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  const run = activeQ.data?.run;
  const validations = run?.validations || [];
  const blocking = validations.filter((v) => v.blocking);
  const nonBlocking = validations.filter((v) => !v.blocking);

  return (
    <div className="border border-brand-blue/40 rounded-lg bg-white p-4 shadow-sm"
         data-testid="cpr-report-run-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Lock size={18} className="text-brand-blue" weight="duotone" />
            <h3 className="text-[16px] font-bold text-slate-800">
              Locked Report Run
            </h3>
            <Badge className="bg-brand-blue text-white text-[9px] tracking-widest">P0</Badge>
          </div>
          <p className="text-[12px] text-slate-500 mt-1">
            Every CPR PDF from SRES is bound to a single immutable report run.
            The run freezes the block model, cut-off, density model and CP
            signatures at the moment it is opened. Regeneration is refused
            if any input has drifted.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => openRunMut.mutate()}
                disabled={openRunMut.isPending}
                data-testid="cpr-report-run-open">
          <ArrowClockwise size={13} className="mr-1" />
          {run ? "Open new run" : "Open first run"}
        </Button>
      </div>

      {activeQ.isLoading && (
        <div className="text-[12px] text-slate-500 animate-pulse" data-testid="cpr-report-run-loading">
          Checking active run…
        </div>
      )}

      {!activeQ.isLoading && !run && (
        <div className="border border-dashed border-slate-300 rounded p-4 text-center text-[12px] text-slate-500"
             data-testid="cpr-report-run-empty">
          <FloppyDisk size={24} className="mx-auto text-slate-400 mb-1" />
          No report run yet. Click <b>Open first run</b> to snapshot the current
          inputs and see what will block publication.
        </div>
      )}

      {run && (
        <>
          {(run.status === "superseded" || run.newer_data_available) && (
            <div className="mb-3 border border-rose-300 bg-rose-50 rounded p-3 flex items-start gap-3"
                 data-testid="cpr-report-run-supersession-banner">
              <Warning size={20} weight="fill" className="text-rose-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-[13px] font-bold text-rose-800">
                  {run.status === "superseded" ? "SUPERSEDED — new data available" : "NEW DATA AVAILABLE"}
                </div>
                <div className="text-[11px] text-rose-700 mt-0.5">
                  This report run was opened before the latest block model or
                  approved density update{run.superseded_by_event
                     ? ` (${run.superseded_by_event.replaceAll("_", " ")})`
                     : ""}. It is retained for audit; publish a new run to
                  reflect the current data.
                </div>
                {run.supersession_reason && (
                  <div className="text-[10px] font-mono text-rose-600 mt-1">
                    reason: {run.supersession_reason}
                  </div>
                )}
              </div>
              <Button className="bg-rose-600 text-white hover:bg-rose-700"
                      onClick={() => setSuccessorPrompt({ runId: run.id })}
                      disabled={successorMut.isPending}
                      data-testid="cpr-report-run-successor">
                <ArrowClockwise size={12} className="mr-1" />
                {successorMut.isPending ? "Opening…" : "Open new run"}
              </Button>
            </div>
          )}

          <div className="flex items-center gap-3 mb-3 text-[12px]" data-testid="cpr-report-run-header">
            <Badge className={
              run.status === "published"
                ? "bg-emerald-600 text-white"
                : run.status === "ready"
                  ? "bg-blue-600 text-white"
                  : run.status === "draft"
                    ? "bg-amber-500 text-white"
                    : "bg-rose-600 text-white"
            } data-testid="cpr-report-run-status">
              {run.status.toUpperCase()}
            </Badge>
            <code className="font-mono text-[11px] text-slate-700"
                   data-testid="cpr-report-run-id">{run.id}</code>
            {run.report_revision && (
              <Badge className="bg-slate-700 text-white text-[9px]"
                      data-testid="cpr-report-run-revision">rev {run.report_revision}</Badge>
            )}
            <span className="text-slate-500">Opened {run.created_at?.slice(0, 19).replace("T", " ")}</span>
            {run.published_at && (
              <span className="text-emerald-600">
                Published {run.published_at?.slice(0, 19).replace("T", " ")}
              </span>
            )}
          </div>

          {run.successor_reason && (
            <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px] text-slate-700 flex items-start gap-2"
                     data-testid="cpr-report-run-successor-reason">
              <ArrowClockwise size={13} className="mt-0.5 text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1.5">Successor reason</span>
                <span className="italic">{run.successor_reason}</span>
                {run.successor_reason_by && (
                  <span className="text-[10px] text-slate-500 ml-2">
                    — {run.successor_reason_by}
                  </span>
                )}
              </div>
            </div>
          )}

          <SnapshotSummary snap={run.snapshot} />
          <GoldUnitBox gold={run.gold_unit} />

          <div className="mt-4 border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
                Validation gates · {validations.length} total, {blocking.length} blocking
              </div>
              <Button variant="outline" size="sm"
                      onClick={() => revalidateMut.mutate(run.id)}
                      disabled={revalidateMut.isPending}
                      data-testid="cpr-report-run-revalidate">
                <ArrowClockwise size={11} className="mr-1" />
                Re-validate
              </Button>
            </div>
            {validations.length === 0 && (
              <div className="text-[12px] text-emerald-700 flex items-center gap-1.5"
                   data-testid="cpr-report-run-all-passed">
                <CheckCircle size={14} weight="fill" /> All validations passed.
              </div>
            )}
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto"
                 data-testid="cpr-report-run-validation-list">
              {blocking.map((v, i) => <ValidationRow key={`b-${i}`} v={v} />)}
              {nonBlocking.map((v, i) => <ValidationRow key={`n-${i}`} v={v} />)}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 pt-3 border-t">
            <Button className="bg-brand-blue text-white"
                    onClick={() => publishMut.mutate(run.id)}
                    disabled={publishMut.isPending || blocking.length > 0}
                    title={blocking.length > 0
                      ? `Clear ${blocking.length} blocking issue(s) first`
                      : "Publish the CPR PDF"}
                    data-testid="cpr-report-run-publish">
              <Signature size={14} className="mr-1.5" weight="duotone" />
              {publishMut.isPending
                ? "Publishing…"
                : blocking.length > 0
                  ? `Publish (blocked · ${blocking.length})`
                  : "Publish CPR PDF"}
            </Button>
            <Button variant="outline"
                    onClick={() => setPreviewRunId(run.id)}
                    disabled={run.status !== "published"}
                    title={run.status !== "published"
                      ? "Publish the CPR PDF first before previewing"
                      : "Preview the compiled CPR PDF inline"}
                    data-testid="cpr-report-run-preview">
              <FilePdf size={14} className="mr-1.5" weight="duotone" />
              Preview
            </Button>
            <Button variant="outline"
                    onClick={() => downloadPdf(run.id)}
                    disabled={run.status !== "published" || busy === "pdf"}
                    data-testid="cpr-report-run-download">
              <FilePdf size={14} className="mr-1.5" weight="duotone" />
              {busy === "pdf" ? "Downloading…" : "Download PDF"}
            </Button>
            <div className="ml-auto text-[10px] font-mono text-slate-400 flex items-center gap-1"
                 data-testid="cpr-report-run-integrity">
              <Lock size={10} /> integrity locked by run id
            </div>
          </div>

          {/* Iter98g · Live compile log — polls the phase document
              written by publish_run so users watch every phase tick
              in real time and failures surface with the exact stage
              that broke. Auto-hides when the compile completes. */}
          <LiveCompileLog pid={pid} runId={run.id}
                              active={publishMut.isPending}
                              autoHide={run.status === "published"}
                              onRetry={(fresh) => {
                                if (fresh) setSuccessorPrompt({ runId: run.id });
                                else       publishMut.mutate(run.id);
                              }}
                              retryDisabled={publishMut.isPending || successorMut.isPending} />
        </>
      )}

      {/* Iter98j · Fresh-snapshot successor confirmation modal */}
      {successorPrompt && (
        <div role="dialog" aria-modal="true"
                 data-testid="cpr-successor-modal"
                 className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4"
                 onClick={(e) => e.target === e.currentTarget && setSuccessorPrompt(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Fresh-snapshot successor</div>
              <h3 className="mt-0.5 font-heading font-bold text-lg tracking-tight text-slate-900">
                Open new run with re-frozen snapshot
              </h3>
              <p className="mt-0.5 text-[12px] text-slate-500">
                A successor run will be created that re-freezes source data from current project state. The failed run is retained as history.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded border border-slate-200 bg-slate-50 p-2 text-[11.5px] text-slate-700">
                <b>Fields that will re-freeze from current project state:</b>
                <ul className="list-disc pl-4 mt-1 space-y-0.5 text-[11px] text-slate-600">
                  <li>Drillhole collar / survey / assay / geology tables</li>
                  <li>Block-model estimates &amp; classification</li>
                  <li>QA/QC populations &amp; density measurements</li>
                  <li>Composites + validation slabs</li>
                </ul>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">
                  Reason <span className="text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  value={successorReason}
                  onChange={(e) => setSuccessorReason(e.target.value)}
                  placeholder="e.g. source data drifted after re-assaying holes SR-023 & SR-024"
                  className="w-full h-8 text-[12px] px-2 border border-slate-200 rounded"
                  autoFocus
                  data-testid="cpr-successor-reason"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50">
              <button type="button"
                          onClick={() => { setSuccessorPrompt(null); setSuccessorReason(""); }}
                          className="text-[12px] text-slate-500 hover:text-slate-800"
                          data-testid="cpr-successor-cancel">
                Cancel
              </button>
              <Button size="sm"
                          disabled={successorMut.isPending || successorReason.trim().length < 3}
                          onClick={() => successorMut.mutate({
                            predecessorId: successorPrompt.runId,
                            reason: successorReason,
                          })}
                          className="h-8 text-[12px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                          data-testid="cpr-successor-confirm">
                {successorMut.isPending ? "Opening…" : "Open successor"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <CPRPreviewModal
        pid={pid}
        runId={previewRunId}
        open={!!previewRunId}
        onClose={() => setPreviewRunId(null)}
        onDownloadFallback={() => previewRunId && downloadPdf(previewRunId)}
      />
    </div>
  );
}
